<template>
    <div class="-z-10 xl:fixed xl:h-screen xl:w-screen xl:pb-36">
        <div class="fixed top-0 hidden h-screen w-screen bg-black/15 xl:block" @click="closePopup()"></div>
        <div v-if="searchQuery.length > 2" class="relative pt-0 xl:mx-12 xl:h-full">
            <div
                id="searchPopup"
                class="flex flex-col bg-white p-8 md:flex-row md:gap-10 xl:h-full xl:gap-20 xl:overflow-y-auto xl:p-14">
                <div class="relative md:w-56 md:shrink-0 xl:w-64 xl:overflow-y-auto xl:pr-3">
                    <div class="absolute top-1 right-0 flex justify-end md:relative md:mb-5 xl:hidden">
                        <button @click="closePopup()" class="flex items-center">
                            Sluiten
                            <svg class="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                <path
                                    d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
                            </svg>
                        </button>
                    </div>
                    <h3 class="mb-5 text-lg font-bold">Merken</h3>
                    <div>
                        <ul v-if="brandSearchResults.length > 0" class="mb-7 border-b-2 border-green-400 pb-7">
                            <li v-for="brandSearchResult in brandSearchResults" :key="brandSearchResult.id">
                                <NuxtLink
                                    :to="'/' + brandSearchResult.seoUrl"
                                    class="mb-2 flex w-full justify-between duration-300 hover:text-green-500 lg:mb-5"
                                    @click="closePopupWithNavigation()">
                                    {{ brandSearchResult.title }}
                                </NuxtLink>
                            </li>
                        </ul>
                        <div class="mb-2 border-b-2 pb-2 lg:mb-7 lg:pb-7" v-else>
                            <span class="mb-2 flex w-full justify-between lg:mb-5"> Geen zoekresultaten gevonden </span>
                        </div>
                    </div>
                    <h3 class="mb-5 text-lg font-bold">Categorieën</h3>
                    <div>
                        <ul
                            v-if="categoriesSearchResult.length > 0"
                            class="mb-4 border-b-2 border-green-400 lg:mb-7 lg:pb-7">
                            <li v-for="category in categoriesSearchResult" :key="category.id">
                                <NuxtLink
                                    :to="'/' + category.seoUrl"
                                    class="mb-2 flex w-full gap-2 duration-300 hover:text-green-500 lg:mb-5"
                                    @click="closePopupWithNavigation()">
                                    {{ category.title || category.name }}
                                    <!-- <span class="text-gray-400">({{ category.nrofresults }})</span> -->
                                </NuxtLink>
                            </li>
                        </ul>
                        <div class="mb-4 border-b-2 border-green-400 pb-4 lg:mb-7 lg:pb-7" v-else>
                            <span class="mb-2 flex w-full justify-between lg:mb-5"> Geen zoekresultaten gevonden </span>
                        </div>
                    </div>
                </div>
                <div class="min-w-0 flex-1 xl:overflow-y-auto">
                    <div class="relative mb-2 items-center lg:mb-10 xl:flex xl:justify-between">
                        <h2 class="mb-5 text-lg font-bold lg:text-3xl xl:mb-0">Artikelen</h2>
                        <button class="btn btn-primary !hidden lg:!block" @click="navigateToShowAll">
                            Toon alle artikelen
                        </button>
                        <button class="absolute top-2 right-0 text-sm underline lg:hidden" @click="navigateToShowAll">
                            Alles tonen
                        </button>
                    </div>
                    <TweakwiseListingResult
                        v-if="productSearchResult"
                        :ids="productSearchResult"
                        :loading="loading"
                        :search="true"
                        :search-term="searchQuery"
                        @closeSearch="closePopupWithNavigation"
                        size="search"
                        classes="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4 xl:gap-6 2xl:gap-4 search-listing" />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    const emit = defineEmits(['closeSearchPopup', 'closeSearchPopupWithNavigation']);
    const loading = ref(true);
    const props = defineProps<{ searchQuery: string }>();
    const productSearchResult: Ref<any[]> = ref([]);
    const categoriesSearchResult: Ref<any[]> = ref([]);
    const brandSearchResults: Ref<any[]> = ref([]);

    let debounceTimeout: NodeJS.Timeout | null = null;

    watch(
        () => props.searchQuery,
        value => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }

            if (value.length < 3) {
                loading.value = false;
                productSearchResult.value = [];
                categoriesSearchResult.value = [];
                brandSearchResults.value = [];
                return;
            }

            debounceTimeout = setTimeout(() => {
                performQuickSearch();
            }, 200);
        },
        { immediate: true },
    );

    function navigateToShowAll() {
        navigateTo('/search?tn_q=' + encodeURIComponent(props.searchQuery));
        emit('closeSearchPopupWithNavigation');
    }

    async function performQuickSearch() {
        loading.value = true;

        try {
            const config = useRuntimeConfig();
            const formData = new FormData();
            formData.append('searchTerm', props.searchQuery);
            formData.append('type', 'tweakwise');
            formData.append('productsLimit', '12');

            const response = await fetch(`${config.public.shopwareEndpoint}quick-search`, {
                method: 'POST',
                headers: {
                    'sw-access-key': config.public.shopwareToken,
                },
                body: formData,
            });

            const data = await response.json();

            // Extract product IDs for TweakwiseListingResult to fetch full product data
            productSearchResult.value = (data.products || []).map((product: any) => product.id);

            // Categories come with seoUrl directly from the API
            categoriesSearchResult.value = data.categories || [];

            // Manufacturers (brands) come with seoUrl directly from the API
            brandSearchResults.value = data.manufacturers || [];
        } catch (error) {
            console.error('Quick search error:', error);
            productSearchResult.value = [];
            categoriesSearchResult.value = [];
            brandSearchResults.value = [];
        }

        loading.value = false;
    }

    function closePopup() {
        emit('closeSearchPopup');
    }

    function closePopupWithNavigation() {
        emit('closeSearchPopupWithNavigation');
    }
</script>
